import React, { useState } from 'react';
import { useShop } from '../context/ShopContext';
import { 
  X, 
  Smartphone, 
  KeyRound, 
  User, 
  MapPin, 
  Mail, 
  Eye, 
  EyeOff, 
  ShieldCheck, 
  Sparkles, 
  Crown,
  ArrowRight,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { 
  isFirebaseConfigured, 
  setupRecaptcha, 
  sendFirebaseOtp, 
  verifyFirebaseOtp 
} from '../services/firebaseAuth';

export default function CustomerAuthModal() {
  const { 
    isCustomerAuthModalOpen, 
    closeCustomerAuthModal, 
    customerAuthMode, 
    openCustomerAuthModal,
    loginCustomer, 
    loginCustomerWithOtp,
    signupCustomer 
  } = useShop();

  const [mode, setMode] = useState(customerAuthMode || 'login'); // 'login' or 'signup'
  const [loginMethod, setLoginMethod] = useState('otp'); // 'otp' or 'pin'
  const [otpStep, setOtpStep] = useState(1); // 1 = Phone input, 2 = OTP verification
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [otpCustomerName, setOtpCustomerName] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [showPin, setShowPin] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [otpNotice, setOtpNotice] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);

  // Login form state
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPin, setLoginPin] = useState('');

  // Signup form state
  const [signupData, setSignupData] = useState({
    name: '',
    phone: '',
    pin: '',
    confirmPin: '',
    email: '',
    city: 'Jaipur',
    address: ''
  });

  // Always default to unified Mobile OTP login when opened
  React.useEffect(() => {
    setMode('login');
    setError('');
  }, [isCustomerAuthModalOpen]);

  // OTP Countdown timer
  React.useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  if (!isCustomerAuthModalOpen) return null;

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setError('');
    const cleanPhone = loginPhone.replace(/[^0-9]/g, '');

    if (cleanPhone.length < 10) {
      setError('Please enter a valid 10-digit mobile number to receive OTP.');
      return;
    }

    if (isFirebaseConfigured()) {
      setIsSubmitting(true);
      try {
        const verifier = setupRecaptcha('firebase-recaptcha-container');
        const confirmation = await sendFirebaseOtp(cleanPhone, verifier);
        setConfirmationResult(confirmation);
        setOtpNotice(`SMS OTP sent to +91 ${cleanPhone}. Please check your messages.`);
        setOtpStep(2);
        setOtpTimer(60);
        setOtpInput('');
      } catch (err) {
        console.error('Firebase OTP Send Error:', err);
        setError(err.message || 'Failed to send SMS OTP via Firebase. Please check your connection or project setup.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Demo OTP fallback when Firebase config is not yet entered
      const code = Math.floor(1000 + Math.random() * 9000).toString();
      setGeneratedOtp(code);
      setOtpNotice(`Demo OTP: ${code} (Default test OTP: 1234 also works)`);
      setOtpStep(2);
      setOtpTimer(30);
      setOtpInput('');
    }
  };

  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    setError('');

    const cleanPhone = loginPhone.replace(/[^0-9]/g, '');
    const cleanOtp = otpInput.trim();

    if (!cleanOtp) {
      setError('Please enter the OTP received.');
      return;
    }

    setIsSubmitting(true);

    try {
      let firebaseUser = null;
      if (confirmationResult && isFirebaseConfigured()) {
        firebaseUser = await verifyFirebaseOtp(confirmationResult, cleanOtp);
      } else {
        // Fallback demo verification
        if (cleanOtp !== generatedOtp && cleanOtp !== '1234' && cleanOtp !== '0000' && cleanOtp !== '123456') {
          setError('Incorrect OTP. Please enter the valid code shown or 1234.');
          setIsSubmitting(false);
          return;
        }
      }

      const res = await loginCustomerWithOtp(cleanPhone, cleanOtp, otpCustomerName, firebaseUser);
      if (!res.success) {
        setError(res.error || 'OTP verification failed. Please try again.');
      }
    } catch (err) {
      console.error('OTP Verification Error:', err);
      setError(err.message || 'Invalid OTP code. Please enter the correct code received on your phone.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');
    const cleanPhone = loginPhone.replace(/[^0-9]/g, '');

    if (cleanPhone.length < 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (!loginPin || loginPin.length < 4) {
      setError('Please enter your 4-digit security PIN.');
      return;
    }

    setIsSubmitting(true);
    const res = await loginCustomer(cleanPhone, loginPin);
    setIsSubmitting(false);

    if (!res.success) {
      setError(res.error || 'Login failed. Please check your credentials.');
    }
  };

  const handleSignup = async (e) => {
    e?.preventDefault();
    setError('');
    const cleanPhone = signupData.phone.replace(/[^0-9]/g, '');

    if (!signupData.name.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (cleanPhone.length < 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (!signupData.pin || signupData.pin.length < 4) {
      setError('PIN must be at least 4 digits.');
      return;
    }
    if (signupData.pin !== signupData.confirmPin) {
      setError('Security PIN and Confirm PIN do not match.');
      return;
    }

    setIsSubmitting(true);
    const res = await signupCustomer({
      name: signupData.name.trim(),
      phone: cleanPhone,
      pin: signupData.pin.trim(),
      email: signupData.email.trim(),
      city: signupData.city.trim() || 'Jaipur',
      address: signupData.address.trim()
    });
    setIsSubmitting(false);

    if (!res.success) {
      setError(res.error || 'Registration failed. Mobile number may already exist.');
    }
  };

  const handleFillDemo = () => {
    setLoginPhone('9829012345');
    setLoginPin('1234');
    setError('');
  };

  return (
    <div 
      onClick={closeCustomerAuthModal}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(5, 12, 28, 0.72)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '1rem',
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '460px',
          backgroundColor: '#ffffff',
          borderRadius: '6px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
          position: 'relative',
          border: '1px solid #e5e7eb'
        }}
      >
        {/* Close Button */}
        <button
          onClick={closeCustomerAuthModal}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#ffffff',
            zIndex: 10,
            transition: 'background 0.15s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          title="Close Modal"
        >
          <X size={18} />
        </button>

        {/* Modal Luxury Royal Header Strip */}
        <div style={{
          background: 'linear-gradient(135deg, #0d2346 0%, #173b75 100%)',
          color: '#ffffff',
          padding: '1.4rem 1.6rem 1.2rem',
          textAlign: 'center',
          position: 'relative'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: 'rgba(255, 229, 0, 0.15)',
            border: '1px solid rgba(255, 229, 0, 0.4)',
            color: '#ffe500',
            marginBottom: '8px'
          }}>
            <Smartphone size={22} />
          </div>
          <h2 style={{
            fontSize: '1.25rem',
            fontFamily: 'var(--font-serif, "Cinzel", Georgia, serif)',
            fontWeight: 700,
            color: '#ffffff',
            margin: '0 0 4px',
            letterSpacing: '0.02em'
          }}>
            M.L. Jewellers
          </h2>
          <p style={{
            margin: '4px 0 0',
            fontSize: '0.82rem',
            color: 'rgba(255, 255, 255, 0.9)'
          }}>
            Login with your 10-Digit Mobile Number & OTP
          </p>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '1.4rem 1.6rem' }}>
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#b91c1c',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '0.82rem',
              marginBottom: '1rem'
            }}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* ========================================================= */}
          {/* LOGIN FORM (Mobile + PIN) */}
          {/* ========================================================= */}
          {/* ========================================================= */}
          {/* LOGIN FORM (OTP or PIN) */}
          {/* ========================================================= */}
          {mode === 'login' && (
            <div>
              {loginMethod === 'otp' ? (
                /* OTP LOGIN FLOW */
                <div>
                  {otpNotice && (
                    <div style={{
                      backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      color: '#1e40af',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      fontSize: '0.82rem',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>💬</span>
                        <span>{otpNotice}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setOtpInput(generatedOtp || '1234')}
                        style={{
                          backgroundColor: '#2563eb',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '3px 8px',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Auto-fill
                      </button>
                    </div>
                  )}

                  {otpStep === 1 ? (
                    /* Step 1: Enter Mobile Number */
                    <form onSubmit={handleSendOtp}>
                      <div style={{ marginBottom: '1.2rem' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          color: '#374151',
                          marginBottom: '6px'
                        }}>
                          Enter 10-Digit Mobile Number <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          backgroundColor: '#ffffff',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            padding: '10px 12px',
                            background: '#f3f4f6',
                            borderRight: '1px solid #d1d5db',
                            fontSize: '0.88rem',
                            fontWeight: 700,
                            color: '#4b5563',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span>🇮🇳</span>
                            <span>+91</span>
                          </div>
                          <input
                            type="tel"
                            maxLength={10}
                            placeholder="e.g. 9829012345"
                            value={loginPhone}
                            onChange={(e) => setLoginPhone(e.target.value.replace(/[^0-9]/g, ''))}
                            autoFocus
                            required
                            style={{
                              flex: 1,
                              border: 'none',
                              outline: 'none',
                              padding: '10px 12px',
                              fontSize: '0.98rem',
                              letterSpacing: '0.04em',
                              fontWeight: 600,
                              color: '#111827'
                            }}
                          />
                        </div>
                        <p style={{ margin: '6px 0 0', fontSize: '0.74rem', color: '#6b7280' }}>
                          We will send a 4-digit verification OTP to this mobile number.
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={loginPhone.length < 10 || isSubmitting}
                        style={{
                          width: '100%',
                          backgroundColor: loginPhone.length === 10 && !isSubmitting ? '#2874f0' : '#93c5fd',
                          color: '#ffffff',
                          border: 'none',
                          padding: '11px',
                          borderRadius: '4px',
                          fontWeight: 700,
                          fontSize: '0.94rem',
                          cursor: loginPhone.length === 10 && !isSubmitting ? 'pointer' : 'not-allowed',
                          boxShadow: '0 2px 6px rgba(40, 116, 240, 0.35)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'background 0.15s ease'
                        }}
                      >
                        {isSubmitting ? (
                          <span>Sending OTP...</span>
                        ) : (
                          <>
                            <span>Send Login OTP</span>
                            <ArrowRight size={16} />
                          </>
                        )}
                      </button>

                      {/* Firebase Invisible Recaptcha Mount Point */}
                      <div id="firebase-recaptcha-container" style={{ marginTop: '4px' }}></div>
                    </form>
                  ) : (
                    /* Step 2: Enter & Verify OTP */
                    <form onSubmit={handleVerifyOtp}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        marginBottom: '1rem',
                        fontSize: '0.82rem'
                      }}>
                        <div style={{ color: '#374151' }}>
                          OTP sent to: <strong>+91 {loginPhone}</strong>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setOtpStep(1); setError(''); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#2874f0',
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontSize: '0.78rem'
                          }}
                        >
                          Change Number
                        </button>
                      </div>

                      <div style={{ marginBottom: '1.2rem' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          color: '#374151',
                          marginBottom: '6px'
                        }}>
                          Enter Verification Code <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          maxLength={6}
                          placeholder="• • • • • •"
                          value={otpInput}
                          onChange={(e) => setOtpInput(e.target.value.replace(/[^0-9]/g, ''))}
                          autoFocus
                          required
                          style={{
                            width: '100%',
                            border: '1.5px solid #2874f0',
                            borderRadius: '4px',
                            outline: 'none',
                            padding: '10px 14px',
                            fontSize: '1.4rem',
                            letterSpacing: '0.35em',
                            textAlign: 'center',
                            fontWeight: 700,
                            color: '#111827',
                            backgroundColor: '#ffffff'
                          }}
                        />

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginTop: '8px',
                          fontSize: '0.76rem'
                        }}>
                          <span style={{ color: '#6b7280' }}>Didn't receive OTP?</span>
                          {otpTimer > 0 ? (
                            <span style={{ color: '#9ca3af' }}>Resend in {otpTimer}s</span>
                          ) : (
                            <button
                              type="button"
                              onClick={handleSendOtp}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#2874f0',
                                fontWeight: 700,
                                cursor: 'pointer',
                                padding: 0
                              }}
                            >
                              Resend OTP
                            </button>
                          )}
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting || !otpInput.trim()}
                        style={{
                          width: '100%',
                          backgroundColor: '#2874f0',
                          color: '#ffffff',
                          border: 'none',
                          padding: '11px',
                          borderRadius: '4px',
                          fontWeight: 700,
                          fontSize: '0.94rem',
                          cursor: isSubmitting || !otpInput.trim() ? 'not-allowed' : 'pointer',
                          boxShadow: '0 2px 6px rgba(40, 116, 240, 0.35)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        {isSubmitting ? (
                          <span>Verifying OTP...</span>
                        ) : (
                          <>
                            <CheckCircle2 size={17} />
                            <span>Verify OTP & Login</span>
                          </>
                        )}
                      </button>
                    </form>
                  )}

                  {/* Switch to PIN Login */}
                  <div style={{
                    marginTop: '1.2rem',
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    color: '#6b7280',
                    borderTop: '1px solid #f3f4f6',
                    paddingTop: '0.9rem'
                  }}>
                    <span>Have a PIN or password? </span>
                    <button
                      type="button"
                      onClick={() => { setLoginMethod('pin'); setError(''); }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#2874f0',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      Login with PIN instead
                    </button>
                  </div>
                </div>
              ) : (
                /* PIN / PASSWORD LOGIN (PRESERVED) */
                <form onSubmit={handleLogin}>
                  {/* Mobile Number Input */}
                  <div style={{ marginBottom: '1.1rem' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '5px'
                    }}>
                      Mobile Number <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        padding: '9px 12px',
                        background: '#f3f4f6',
                        borderRight: '1px solid #d1d5db',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        color: '#4b5563',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span>🇮🇳</span>
                        <span>+91</span>
                      </div>
                      <input
                        type="tel"
                        maxLength={10}
                        placeholder="Enter 10-digit mobile"
                        value={loginPhone}
                        onChange={(e) => setLoginPhone(e.target.value.replace(/[^0-9]/g, ''))}
                        required
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          padding: '9px 12px',
                          fontSize: '0.92rem',
                          letterSpacing: '0.04em',
                          fontWeight: 600,
                          color: '#111827'
                        }}
                      />
                    </div>
                  </div>

                  {/* 4-Digit Security PIN */}
                  <div style={{ marginBottom: '1.2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                      <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>
                        4-Digit Security PIN <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <button
                        type="button"
                        onClick={handleFillDemo}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#2874f0',
                          fontSize: '0.74rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          padding: 0
                        }}
                      >
                        Use Demo (PIN: 1234)
                      </button>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      padding: '0 10px',
                      backgroundColor: '#ffffff'
                    }}>
                      <KeyRound size={16} color="#9ca3af" />
                      <input
                        type={showPin ? 'text' : 'password'}
                        maxLength={6}
                        placeholder="Enter 4-digit PIN"
                        value={loginPin}
                        onChange={(e) => setLoginPin(e.target.value)}
                        required
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          padding: '9px 10px',
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          letterSpacing: '0.2em',
                          color: '#111827'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPin(!showPin)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          padding: '4px'
                        }}
                      >
                        {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      backgroundColor: '#2874f0',
                      color: '#ffffff',
                      border: 'none',
                      padding: '11px',
                      borderRadius: '4px',
                      fontWeight: 700,
                      fontSize: '0.92rem',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      boxShadow: '0 2px 6px rgba(40, 116, 240, 0.35)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'background 0.15s ease'
                    }}
                  >
                    {isSubmitting ? (
                      <span>Logging in...</span>
                    ) : (
                      <>
                        <span>Login with Mobile & PIN</span>
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  {/* Switch to OTP Login */}
                  <div style={{
                    marginTop: '1.2rem',
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    color: '#6b7280',
                    borderTop: '1px solid #f3f4f6',
                    paddingTop: '0.9rem'
                  }}>
                    <span>Prefer faster login? </span>
                    <button
                      type="button"
                      onClick={() => { setLoginMethod('otp'); setError(''); }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#2874f0',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      ⚡ Instant Login with OTP
                    </button>
                  </div>
                </form>
              )}

              {/* Modal Footer Link */}
              <div style={{
                marginTop: '1.2rem',
                textAlign: 'center',
                borderTop: '1px solid #f3f4f6',
                paddingTop: '0.9rem'
              }}>
                <button
                  type="button"
                  onClick={closeCustomerAuthModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    textDecoration: 'underline'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#1f2937'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                >
                  Skip for now & browse as guest →
                </button>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SIGNUP FORM */}
          {/* ========================================================= */}
          {mode === 'signup' && (
            <form onSubmit={handleSignup}>
              {/* Full Name */}
              <div style={{ marginBottom: '0.9rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                  Full Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '4px', padding: '0 10px' }}>
                  <User size={16} color="#9ca3af" />
                  <input
                    type="text"
                    placeholder="e.g. Ramesh Chandra Verma"
                    value={signupData.name}
                    onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                    required
                    style={{ flex: 1, border: 'none', outline: 'none', padding: '8px 10px', fontSize: '0.88rem' }}
                  />
                </div>
              </div>

              {/* Mobile Number */}
              <div style={{ marginBottom: '0.9rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                  Mobile Number (For Login) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ padding: '8px 10px', background: '#f3f4f6', borderRight: '1px solid #d1d5db', fontSize: '0.82rem', fontWeight: 700, color: '#4b5563' }}>
                    +91
                  </div>
                  <input
                    type="tel"
                    maxLength={10}
                    placeholder="10-digit mobile"
                    value={signupData.phone}
                    onChange={(e) => setSignupData({ ...signupData, phone: e.target.value.replace(/[^0-9]/g, '') })}
                    required
                    style={{ flex: 1, border: 'none', outline: 'none', padding: '8px 10px', fontSize: '0.88rem', fontWeight: 600 }}
                  />
                </div>
              </div>

              {/* PIN & Confirm PIN Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '0.9rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    Security PIN (4 digits) <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="password"
                    maxLength={6}
                    placeholder="e.g. 1234"
                    value={signupData.pin}
                    onChange={(e) => setSignupData({ ...signupData, pin: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      padding: '8px 10px',
                      fontSize: '0.88rem',
                      letterSpacing: '0.15em',
                      fontWeight: 700
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    Confirm PIN <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="password"
                    maxLength={6}
                    placeholder="Re-enter PIN"
                    value={signupData.confirmPin}
                    onChange={(e) => setSignupData({ ...signupData, confirmPin: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      padding: '8px 10px',
                      fontSize: '0.88rem',
                      letterSpacing: '0.15em',
                      fontWeight: 700
                    }}
                  />
                </div>
              </div>

              {/* City & Address */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '1.1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    City
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Jaipur"
                    value={signupData.city}
                    onChange={(e) => setSignupData({ ...signupData, city: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: '4px', padding: '8px 10px', fontSize: '0.85rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    Email (Optional)
                  </label>
                  <input
                    type="email"
                    placeholder="For GST bills"
                    value={signupData.email}
                    onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: '4px', padding: '8px 10px', fontSize: '0.85rem' }}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  backgroundColor: '#0d2346',
                  color: '#ffe500',
                  border: '1px solid #e5ce00',
                  padding: '11px',
                  borderRadius: '4px',
                  fontWeight: 700,
                  fontSize: '0.92rem',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 2px 6px rgba(13, 35, 70, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isSubmitting ? (
                  <span>Registering Account...</span>
                ) : (
                  <>
                    <span>👑 Register Customer Account</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              <div style={{
                marginTop: '1rem',
                textAlign: 'center',
                fontSize: '0.8rem',
                color: '#6b7280'
              }}>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2874f0',
                    fontWeight: 700,
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  Login with Mobile & PIN
                </button>
                <div style={{ marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={closeCustomerAuthModal}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#9ca3af',
                      fontSize: '0.78rem',
                      cursor: 'pointer',
                      padding: '2px 6px',
                      textDecoration: 'underline'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#4b5563'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                  >
                    Skip for now & browse as guest →
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Trust Banner at Bottom */}
          <div style={{
            marginTop: '1.2rem',
            paddingTop: '0.8rem',
            borderTop: '1px solid #f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '14px',
            fontSize: '0.72rem',
            color: '#6b7280'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ShieldCheck size={13} color="#10b981" /> 100% Encrypted PIN
            </span>
            <span>•</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Sparkles size={13} color="#d4af37" /> BIS HUID Hallmarked
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
